import React from "react";
import type { OrTabsVariant } from "./or-tabs";

export interface UseOrTabsParams<TOption> {
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  getOptionDisabled?: (option: TOption) => boolean;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  onChange?: (nextValue: TOption) => void;
  options: readonly TOption[];
  value?: TOption | null;
  variant?: OrTabsVariant;
}

export interface UseOrTabsResult<TOption> {
  activeIndex: number;
  focusableIndex: number;
  handleTabClick: (index: number) => void;
  handleTabKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => void;
  handleViewportPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleViewportPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleViewportPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleViewportPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  indicatorStyle: React.CSSProperties;
  isOptionDisabled: (option: TOption) => boolean;
  listClassName: string;
  listRef: React.MutableRefObject<HTMLDivElement | null>;
  setTabRef: (index: number, node: HTMLButtonElement | null) => void;
  shouldShowIndicator: boolean;
  tabClassName: (isActive: boolean, isDisabled: boolean) => string;
  viewportClassName: string;
  viewportRef: React.MutableRefObject<HTMLDivElement | null>;
  wrapperClassName: string;
}

export function useOrTabs<TOption>({
  className,
  disabled = false,
  fullWidth = true,
  getOptionDisabled,
  isOptionEqualToValue = (option, nextValue) => option === nextValue,
  onChange,
  options,
  value,
  variant = "default",
}: UseOrTabsParams<TOption>): UseOrTabsResult<TOption> {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const dragStateRef = React.useRef({
    hasCapturedPointer: false,
    hasDragged: false,
    pointerId: null as number | null,
    scrollLeft: 0,
    startX: 0,
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const [indicatorMetrics, setIndicatorMetrics] = React.useState({
    ready: false,
    width: 0,
    x: 0,
  });

  const isOptionDisabled = React.useCallback(
    (option: TOption) => disabled || getOptionDisabled?.(option) === true,
    [disabled, getOptionDisabled],
  );

  const activeIndex = React.useMemo(() => {
    if (value === undefined || value === null) {
      return -1;
    }

    return options.findIndex((option) => isOptionEqualToValue(option, value));
  }, [isOptionEqualToValue, options, value]);

  const focusableIndex = React.useMemo(() => {
    if (activeIndex >= 0 && !isOptionDisabled(options[activeIndex])) {
      return activeIndex;
    }

    return options.findIndex((option) => !isOptionDisabled(option));
  }, [activeIndex, isOptionDisabled, options]);

  const updateIndicator = React.useCallback(() => {
    const activeTab = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;

    if (!activeTab) {
      setIndicatorMetrics((previousMetrics) => {
        if (!previousMetrics.ready && previousMetrics.width === 0 && previousMetrics.x === 0) {
          return previousMetrics;
        }

        return {
          ready: false,
          width: 0,
          x: 0,
        };
      });
      return;
    }

    setIndicatorMetrics({
      ready: true,
      width: activeTab.offsetWidth,
      x: activeTab.offsetLeft,
    });
  }, [activeIndex]);

  React.useLayoutEffect(() => {
    const animationFrameId = window.requestAnimationFrame(updateIndicator);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [updateIndicator]);

  React.useEffect(() => {
    const activeTab = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;

    if (!activeTab || typeof activeTab.scrollIntoView !== "function") {
      return;
    }

    activeTab.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeIndex]);

  React.useEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener("resize", handleResize);

    if (typeof ResizeObserver === "undefined") {
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateIndicator();
    });

    if (listRef.current) {
      resizeObserver.observe(listRef.current);
    }

    const activeTab = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;

    if (activeTab) {
      resizeObserver.observe(activeTab);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [activeIndex, updateIndicator]);

  const findNextEnabledIndex = React.useCallback(
    (startIndex: number, direction: 1 | -1): number => {
      if (options.length === 0) {
        return -1;
      }

      let candidateIndex = startIndex;

      for (let attempt = 0; attempt < options.length; attempt += 1) {
        candidateIndex = (candidateIndex + direction + options.length) % options.length;

        if (!isOptionDisabled(options[candidateIndex])) {
          return candidateIndex;
        }
      }

      return -1;
    },
    [isOptionDisabled, options],
  );

  const findBoundaryEnabledIndex = React.useCallback(
    (direction: "start" | "end"): number => {
      const candidateOptions = direction === "start" ? options : [...options].reverse();
      const locatedIndex = candidateOptions.findIndex((option) => !isOptionDisabled(option));

      if (locatedIndex < 0) {
        return -1;
      }

      return direction === "start" ? locatedIndex : options.length - locatedIndex - 1;
    },
    [isOptionDisabled, options],
  );

  const focusTab = React.useCallback((index: number) => {
    tabRefs.current[index]?.focus();
  }, []);

  const selectTab = React.useCallback(
    (index: number) => {
      const nextOption = options[index];

      if (!nextOption || isOptionDisabled(nextOption) || index === activeIndex) {
        return;
      }

      onChange?.(nextOption);
    },
    [activeIndex, isOptionDisabled, onChange, options],
  );

  const handleTabClick = React.useCallback(
    (index: number) => {
      if (dragStateRef.current.hasDragged) {
        return;
      }

      selectTab(index);
    },
    [selectTab],
  );

  const handleTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown": {
          event.preventDefault();
          const nextIndex = findNextEnabledIndex(index, 1);

          if (nextIndex >= 0) {
            focusTab(nextIndex);
            selectTab(nextIndex);
          }
          break;
        }
        case "ArrowLeft":
        case "ArrowUp": {
          event.preventDefault();
          const previousIndex = findNextEnabledIndex(index, -1);

          if (previousIndex >= 0) {
            focusTab(previousIndex);
            selectTab(previousIndex);
          }
          break;
        }
        case "Home": {
          event.preventDefault();
          const firstEnabledIndex = findBoundaryEnabledIndex("start");

          if (firstEnabledIndex >= 0) {
            focusTab(firstEnabledIndex);
            selectTab(firstEnabledIndex);
          }
          break;
        }
        case "End": {
          event.preventDefault();
          const lastEnabledIndex = findBoundaryEnabledIndex("end");

          if (lastEnabledIndex >= 0) {
            focusTab(lastEnabledIndex);
            selectTab(lastEnabledIndex);
          }
          break;
        }
        case " ":
        case "Enter": {
          event.preventDefault();
          selectTab(index);
          break;
        }
        default:
          break;
      }
    },
    [findBoundaryEnabledIndex, findNextEnabledIndex, focusTab, selectTab],
  );

  const handleViewportPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    dragStateRef.current.pointerId = event.pointerId;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.scrollLeft = event.currentTarget.scrollLeft;
    dragStateRef.current.hasCapturedPointer = false;
    dragStateRef.current.hasDragged = false;
  }, []);

  const handleViewportPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (Math.abs(deltaX) > 6) {
      if (!dragStateRef.current.hasCapturedPointer && typeof event.currentTarget.setPointerCapture === "function") {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStateRef.current.hasCapturedPointer = true;
      }

      dragStateRef.current.hasDragged = true;
      setIsDragging(true);
      event.currentTarget.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
    }
  }, []);

  const finishPointerInteraction = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    if (
      dragStateRef.current.hasCapturedPointer &&
      typeof event.currentTarget.hasPointerCapture === "function" &&
      event.currentTarget.hasPointerCapture(event.pointerId) &&
      typeof event.currentTarget.releasePointerCapture === "function"
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.hasCapturedPointer = false;
    dragStateRef.current.pointerId = null;

    window.setTimeout(() => {
      dragStateRef.current.hasDragged = false;
      setIsDragging(false);
    }, 0);
  }, []);

  const wrapperClassName = ["or-tabs", `or-tabs--${variant}`, fullWidth ? "or-tabs--full-width" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  const viewportClassName = [
    "or-tabs__viewport",
    disabled ? "or-tabs__viewport--disabled" : "",
    isDragging ? "or-tabs__viewport--dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const listClassName = ["or-tabs__list", indicatorMetrics.ready ? "or-tabs__list--ready" : ""]
    .filter(Boolean)
    .join(" ");

  const tabClassName = (isActive: boolean, isDisabledOption: boolean) =>
    ["or-tabs__tab", isActive ? "or-tabs__tab--active" : "", isDisabledOption ? "or-tabs__tab--disabled" : ""]
      .filter(Boolean)
      .join(" ");

  const indicatorStyle: React.CSSProperties = {
    transform: `translateX(${indicatorMetrics.x}px)`,
    width: indicatorMetrics.width > 0 ? `${indicatorMetrics.width}px` : undefined,
  };

  const setTabRef = React.useCallback((index: number, node: HTMLButtonElement | null) => {
    tabRefs.current[index] = node;
  }, []);

  return {
    activeIndex,
    focusableIndex,
    handleTabClick,
    handleTabKeyDown,
    handleViewportPointerCancel: finishPointerInteraction,
    handleViewportPointerDown,
    handleViewportPointerMove,
    handleViewportPointerUp: finishPointerInteraction,
    indicatorStyle,
    isOptionDisabled,
    listClassName,
    listRef,
    setTabRef,
    shouldShowIndicator: indicatorMetrics.ready,
    tabClassName,
    viewportClassName,
    viewportRef,
    wrapperClassName,
  };
}
